import { ChangeDetectionStrategy, ChangeDetectorRef, Component, computed, inject, input, output, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { NzSelectModule } from 'ng-zorro-antd/select';
import { NzInputModule } from 'ng-zorro-antd/input';
import { NzSliderModule } from 'ng-zorro-antd/slider';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzCodeEditorModule } from 'ng-zorro-antd/code-editor';
import { NzCollapseModule } from 'ng-zorro-antd/collapse';
import { NzInputNumberModule } from 'ng-zorro-antd/input-number';
import { NzTooltipModule } from 'ng-zorro-antd/tooltip';
import { NzPopconfirmModule } from 'ng-zorro-antd/popconfirm';
import { NzModalModule } from 'ng-zorro-antd/modal';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { CdkDragDrop, CdkDrag, CdkDropList, moveItemInArray } from '@angular/cdk/drag-drop';
import { FikoYTId } from '@/src/service/fikoyt-ui/helpers/FikoYTId';
import type { ISablon, ISablonVeriKaynagi, ISablonItem, ISablonOlaylar } from '../sablon-editoru/sablon.interface';

interface IBilesenPreset {
  id: string;
  simge: string;
  baslik: string;
  aciklama: string;
  varsayilan: Partial<ISablonItem>;
}

interface IBilesenKategori {
  kategori: string;
  bilesenler: IBilesenPreset[];
}

const VERI_MOD_BILGI: Record<string, { simge: string; etiket: string; aciklama: string }> = {
  aggregate: { simge: '📊', etiket: 'Aggregate', aciklama: 'MongoDB aggregate pipeline ile veri çek' },
  sabit: { simge: '📄', etiket: 'Sabit', aciklama: 'Sabit JSON veri kullan' },
  soket: { simge: '🔌', etiket: 'Socket.IO', aciklama: 'Socket.IO event dinle (gerçek zamanlı)' },
  hesaplama: { simge: '🧮', etiket: 'Hesaplama', aciklama: 'JavaScript fonksiyonu ile hesapla' },
};

const BILESEN_KUTUPHANESI: IBilesenKategori[] = [
  {
    kategori: 'Navigasyon',
    bilesenler: [
      { id: 'nav-sayfa', simge: '📄', baslik: 'Sayfa Linki', aciklama: 'Bir sayfaya yönlendir', varsayilan: { olaylar: { tiklanma: "router.navigate(['/path'])" } } },
      { id: 'nav-url', simge: '🔗', baslik: 'Dış Link', aciklama: 'Harici URL aç', varsayilan: { olaylar: { tiklanma: "window.open('https://example.com', '_blank')" } } },
      { id: 'nav-geri', simge: '←', baslik: 'Geri', aciklama: 'Önceki sayfaya dön', varsayilan: { olaylar: { tiklanma: 'history.back()' } } },
      { id: 'nav-modal', simge: '📦', baslik: 'Modal Aç', aciklama: 'Modal pencere aç', varsayilan: { olaylar: { tiklanma: "bildirim('Modal açıldı')" } } },
    ],
  },
  {
    kategori: 'Veri Gösterim',
    bilesenler: [
      { id: 'veri-sayac', simge: '🔢', baslik: 'Sayaç', aciklama: 'Büyük sayı göster', varsayilan: { veriKaynagi: { mod: 'sabit', sabitVeri: { deger: 42 } } } },
      { id: 'veri-ilerleme', simge: '📊', baslik: 'İlerleme', aciklama: 'Yüzde ilerleme', varsayilan: { veriKaynagi: { mod: 'sabit', sabitVeri: { yuzde: 65 } } } },
      { id: 'veri-durum', simge: '🔴', baslik: 'Durum', aciklama: 'Açık/Kapalı durumu', varsayilan: {} },
      { id: 'veri-badge', simge: '🏷', baslik: 'Badge', aciklama: 'Etiket rozeti', varsayilan: {} },
    ],
  },
  {
    kategori: 'Aksiyon',
    bilesenler: [
      { id: 'aks-buton', simge: '▶', baslik: 'Buton', aciklama: 'Tıklanabilir buton', varsayilan: { olaylar: { tiklanma: "bildirim('Tıklandı!', 'basarili')" } } },
      { id: 'aks-toggle', simge: '🔘', baslik: 'Toggle', aciklama: 'Açma/kapama anahtarı', varsayilan: {} },
      { id: 'aks-bildirim', simge: '🔔', baslik: 'Bildirim', aciklama: 'Bildirim tetikle', varsayilan: { olaylar: { tiklanma: "bildirim('Yeni bildirim!', 'uyari')" } } },
      { id: 'aks-yenile', simge: '🔄', baslik: 'Yenile', aciklama: 'Veriyi yenile', varsayilan: { olaylar: { tiklanma: 'yenile()' } } },
    ],
  },
  {
    kategori: 'Medya',
    bilesenler: [
      { id: 'med-resim', simge: '🖼', baslik: 'Resim', aciklama: 'Resim göster', varsayilan: {} },
      { id: 'med-avatar', simge: '👤', baslik: 'Avatar', aciklama: 'Kullanıcı avatarı', varsayilan: {} },
      { id: 'med-emoji', simge: '😀', baslik: 'Emoji', aciklama: 'Emoji göster', varsayilan: {} },
      { id: 'med-ikon', simge: '⭐', baslik: 'İkon', aciklama: 'İkon göster', varsayilan: {} },
    ],
  },
  {
    kategori: 'Parametreler',
    bilesenler: [
      { id: 'prm-metin', simge: '📝', baslik: 'Metin', aciklama: 'Metin parametresi', varsayilan: {} },
      { id: 'prm-sayi', simge: '🔢', baslik: 'Sayı', aciklama: 'Sayısal parametre', varsayilan: {} },
      { id: 'prm-secenek', simge: '📋', baslik: 'Seçenek', aciklama: 'Seçim listesi', varsayilan: {} },
      { id: 'prm-tarih', simge: '📅', baslik: 'Tarih', aciklama: 'Tarih parametresi', varsayilan: {} },
    ],
  },
];

const OLAY_SABLONLAR: Record<string, { baslik: string; sablon: string }[]> = {
  tiklanma: [
    { baslik: 'Yönlendirme', sablon: "router.navigate(['/path'])" },
    { baslik: 'Bildirim', sablon: "bildirim('Mesaj', 'basarili')" },
    { baslik: 'Güncelle', sablon: 'guncelle({key: value})' },
    { baslik: 'Harici URL', sablon: "window.open('https://example.com', '_blank')" },
    { baslik: 'Veriyi logla', sablon: "console.log('veri:', veri)" },
  ],
  yenilendi: [
    { baslik: 'Eşik kontrol', sablon: "if (veri?.sayi > 100) bildirim('Eşik aşıldı')" },
    { baslik: 'Başlığa yaz', sablon: "guncelle({ baslik: 'Toplam: ' + (veri?.sayi || 0) })" },
  ],
  esikAsimi: [
    { baslik: 'Uyarı', sablon: "bildirim('Dikkat: ' + JSON.stringify(veri), 'hata')" },
  ],
  ciftTiklama: [
    { baslik: 'Detay aç', sablon: "router.navigate(['/detay/' + veri?.id])" },
    { baslik: 'Kopyala', sablon: "navigator.clipboard.writeText(JSON.stringify(veri, null, 2))" },
  ],
};

@Component({
  selector: 'te-veri-paneli',
  standalone: true,
  imports: [
    CommonModule, FormsModule,
    NzSelectModule, NzInputModule, NzSliderModule,
    NzButtonModule, NzCodeEditorModule, NzCollapseModule,
    NzInputNumberModule, NzTooltipModule, NzPopconfirmModule,
    NzModalModule, NzIconModule,
    CdkDrag, CdkDropList,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div class="veri-paneli">

      <div class="bolum">
        <div class="bolum-baslik">Veri Kaynağı</div>

        <div class="mod-kartlar">
          @for (mod of modListesi; track mod) {
            <button class="mod-kart" [class.secili]="seciliMod() === mod"
                    (click)="modDegistir(mod)">
              <span class="mod-simge">{{ veriModBilgi[mod].simge }}</span>
              <span class="mod-ad">{{ veriModBilgi[mod].etiket }}</span>
            </button>
          }
        </div>
        <div class="mod-aciklama">{{ veriModBilgi[seciliMod()].aciklama }}</div>

        @switch (seciliMod()) {
          @case ('aggregate') {
            <div class="alan-grup">
              <label class="alan-etiket">Koleksiyon (VT)</label>
              <input nz-input nzSize="small" [ngModel]="vkVt()" (ngModelChange)="vkAlanDegistir('vt', $event)" placeholder="FikoYT.sistem.xxx" />
            </div>
            <div class="alan-grup">
              <label class="alan-etiket">Pipeline (JSON)</label>
              <div class="kod-onizleme" (click)="kodEditorAc('Pipeline (JSON)', pipelineStr(), 'json', 'pipeline')">
                <pre>{{ pipelineStr() | slice:0:120 }}{{ (pipelineStr()?.length || 0) > 120 ? '...' : '' }}</pre>
                <button nz-button nzSize="small" nzType="link"><span nz-icon nzType="edit"></span> Düzenle</button>
              </div>
            </div>
            <div class="alan-grup">
              <label class="alan-etiket">Dönüşüm Fonksiyonu</label>
              <div class="kod-onizleme" (click)="kodEditorAc('Dönüşüm Fonksiyonu', vkDonusumFunc(), 'javascript', 'donusum')">
                <pre>{{ vkDonusumFunc() | slice:0:120 }}{{ (vkDonusumFunc()?.length || 0) > 120 ? '...' : '' }}</pre>
                <button nz-button nzSize="small" nzType="link"><span nz-icon nzType="edit"></span> Düzenle</button>
              </div>
              <div class="kod-ipucu">Parametreler: <code>veri</code>, <code>sablon</code></div>
            </div>
            <div class="alan-grup">
              <label class="alan-etiket">Soket Tetik (opsiyonel)</label>
              <input nz-input nzSize="small" [ngModel]="vkSoketTetik()" (ngModelChange)="vkAlanDegistir('soketTetik', $event)"
                placeholder="Veri değiştiğinde aggregate yeniden çalışsın" />
            </div>
          }
          @case ('sabit') {
            <div class="alan-grup">
              <label class="alan-etiket">Sabit Veri (JSON)</label>
              <div class="kod-onizleme" (click)="kodEditorAc('Sabit Veri (JSON)', sabitVeriStr(), 'json', 'sabit')">
                <pre>{{ sabitVeriStr() | slice:0:120 }}{{ (sabitVeriStr()?.length || 0) > 120 ? '...' : '' }}</pre>
                <button nz-button nzSize="small" nzType="link"><span nz-icon nzType="edit"></span> Düzenle</button>
              </div>
            </div>
          }
          @case ('soket') {
            <div class="alan-grup">
              <label class="alan-etiket">Event Adı</label>
              <input nz-input nzSize="small" [ngModel]="vkSoketOlay()" (ngModelChange)="vkAlanDegistir('soketOlay', $event)"
                placeholder="FikoYT.sistem.io.cihaz.guncellendi" />
            </div>
            <div class="alan-grup">
              <label class="alan-etiket">Filtre (JSON, opsiyonel)</label>
              <div class="kod-onizleme" (click)="kodEditorAc('Soket Filtre (JSON)', soketFiltreStr(), 'json', 'soketFiltre')">
                <pre>{{ soketFiltreStr() | slice:0:120 }}{{ (soketFiltreStr()?.length || 0) > 120 ? '...' : '' }}</pre>
                <button nz-button nzSize="small" nzType="link"><span nz-icon nzType="edit"></span> Düzenle</button>
              </div>
            </div>
          }
          @case ('hesaplama') {
            <div class="alan-grup">
              <label class="alan-etiket">Hesaplama Fonksiyonu (JS)</label>
              <div class="kod-onizleme" (click)="kodEditorAc('Hesaplama Fonksiyonu (JS)', vkPiFunc(), 'javascript', 'hesaplama')">
                <pre>{{ vkPiFunc() | slice:0:120 }}{{ (vkPiFunc()?.length || 0) > 120 ? '...' : '' }}</pre>
                <button nz-button nzSize="small" nzType="link"><span nz-icon nzType="edit"></span> Düzenle</button>
              </div>
              <div class="kod-ipucu">Parametreler: <code>veri</code>, <code>sablon</code> &middot; Dönüş: JSON obje</div>
            </div>
          }
        }

        <div class="alan-grup">
          <label class="alan-etiket">Yenileme Aralığı: {{ vkYenilemeAraligi() || 0 }}sn</label>
          <nz-slider [ngModel]="vkYenilemeAraligi() || 0" (ngModelChange)="vkAlanDegistir('yenilemeAraligi', $event)"
            [nzMin]="0" [nzMax]="300" [nzStep]="5"></nz-slider>
        </div>

        <button nz-button nzType="dashed" nzSize="small" (click)="veriTestTiklandi.emit()" class="test-btn">
          🧪 Veri Testi Yap
        </button>
      </div>

      <div class="bolum">
        <div class="bolum-baslik">İtemler</div>

        <div class="kutuphane-grid">
          @for (kat of bilesenKutuphanesi; track kat.kategori) {
            <div class="kutuphane-kategori">
              <span class="kutuphane-kat-baslik">{{ kat.kategori }}</span>
              <div class="kutuphane-bilesenler">
                @for (b of kat.bilesenler; track b.id) {
                  <button class="kutuphane-btn" (click)="bilesendenEkle(b)" [nz-tooltip]="b.aciklama">
                    <span class="kb-simge">{{ b.simge }}</span>
                    <span class="kb-ad">{{ b.baslik }}</span>
                  </button>
                }
              </div>
            </div>
          }
        </div>

        <div class="item-ust-bar">
          <span class="item-sayi">{{ mevcutItemler().length }} item</span>
          <button nz-button nzSize="small" nzType="dashed" (click)="bosItemEkle()">+ Boş Item</button>
        </div>

        @if (mevcutItemler().length) {
          <div cdkDropList (cdkDropListDropped)="onItemDrop($event)" class="item-liste">
            @for (item of mevcutItemler(); track item.id; let i = $index) {
              <div class="item-kart" cdkDrag [class.acik]="seciliItemIdx() === i">
                <div class="item-kart-baslik" cdkDragHandle>
                  <span class="item-drag-handle">⠿</span>
                  <span class="item-simge-onizleme">{{ item['simge'] || '📦' }}</span>
                  <span class="item-baslik-metin">{{ item['baslik'] || 'İtem ' + (i + 1) }}</span>
                  <span class="item-bosluk"></span>
                  <button class="item-btn sil" nz-popconfirm nzPopconfirmTitle="Bu item silinsin mi?"
                    (nzOnConfirm)="itemSil(i)">✕</button>
                  <button class="item-btn ac-kapat" (click)="seciliItemIdx.set(seciliItemIdx() === i ? -1 : i)">
                    {{ seciliItemIdx() === i ? '▲' : '▼' }}
                  </button>
                </div>

                @if (seciliItemIdx() === i) {
                  <div class="item-detay">
                    <div class="item-bolum">
                      <div class="item-bolum-baslik">Genel</div>
                      <div class="item-alan-grid">
                        <div class="alan-mini">
                          <label>Simge</label>
                          <input nz-input nzSize="small" [(ngModel)]="item['simge']" placeholder="📦" (ngModelChange)="itemlerYayinla()" />
                        </div>
                        <div class="alan-mini">
                          <label>Başlık</label>
                          <input nz-input nzSize="small" [(ngModel)]="item['baslik']" placeholder="İtem adı" (ngModelChange)="itemlerYayinla()" />
                        </div>
                      </div>
                      <div class="alan-mini">
                        <label>Açıklama</label>
                        <input nz-input nzSize="small" [(ngModel)]="item['aciklama']" placeholder="Kısa açıklama" (ngModelChange)="itemlerYayinla()" />
                      </div>
                    </div>

                    <div class="item-bolum">
                      <div class="item-bolum-baslik">Tasarım</div>
                      <div class="alan-mini">
                        <label>Renk</label>
                        <div class="renk-satir">
                          <input type="color" [ngModel]="item['renk'] || '#1890ff'" (ngModelChange)="item['renk'] = $event; itemlerYayinla()" class="renk-secici" />
                          <input nz-input nzSize="small" [(ngModel)]="item['renk']" (ngModelChange)="itemlerYayinla()" />
                        </div>
                      </div>
                      <div class="alan-mini">
                        <label>Gradyan</label>
                        <input nz-input nzSize="small" [(ngModel)]="item['gradyan']" placeholder="linear-gradient(...)" (ngModelChange)="itemlerYayinla()" />
                      </div>
                      <div class="alan-mini">
                        <label>Yazı Rengi</label>
                        <div class="renk-satir">
                          <input type="color" [ngModel]="item['renkYazi'] || '#ffffff'" (ngModelChange)="item['renkYazi'] = $event; itemlerYayinla()" class="renk-secici" />
                          <input nz-input nzSize="small" [(ngModel)]="item['renkYazi']" placeholder="#ffffff" (ngModelChange)="itemlerYayinla()" />
                        </div>
                      </div>
                      <div class="alan-mini">
                        <label>Köşe Yuvarlak: {{ item['borderRadius'] || 8 }}px</label>
                        <nz-slider [(ngModel)]="item['borderRadius']" [nzMin]="0" [nzMax]="24" [nzStep]="2" (ngModelChange)="itemlerYayinla()"></nz-slider>
                      </div>
                    </div>

                    <div class="item-bolum">
                      <div class="item-bolum-baslik">Veri Kaynağı</div>
                      <div class="alan-mini">
                        <label>Mod</label>
                        <nz-select [ngModel]="item.veriKaynagi?.mod || 'sabit'" (ngModelChange)="itemVeriModDegistir(item, $event)" nzSize="small" style="width: 100%">
                          <nz-option nzValue="sabit" nzLabel="Sabit"></nz-option>
                          <nz-option nzValue="aggregate" nzLabel="Aggregate"></nz-option>
                          <nz-option nzValue="soket" nzLabel="Socket.IO"></nz-option>
                          <nz-option nzValue="hesaplama" nzLabel="Hesaplama"></nz-option>
                        </nz-select>
                      </div>
                      @if (item.veriKaynagi?.mod === 'sabit') {
                        <div class="alan-mini">
                          <label>Sabit Veri (JSON)</label>
                          <div class="kod-onizleme" (click)="kodEditorAc('Sabit Veri (JSON)', item['_sabitVeriStr'] || '', 'json', 'itemSabit', item)">
                            <pre>{{ (item['_sabitVeriStr'] || '') | slice:0:80 }}{{ (item['_sabitVeriStr']?.length || 0) > 80 ? '...' : '' }}</pre>
                            <button nz-button nzSize="small" nzType="link"><span nz-icon nzType="edit"></span></button>
                          </div>
                        </div>
                      }
                      @if (item.veriKaynagi?.mod === 'aggregate') {
                        <div class="alan-mini">
                          <label>VT</label>
                          <input nz-input nzSize="small" [ngModel]="item.veriKaynagi?.vt || ''" (ngModelChange)="itemVeriAlanDegistir(item, 'vt', $event)" />
                        </div>
                        <div class="alan-mini">
                          <label>Pipeline (JSON)</label>
                          <div class="kod-onizleme" (click)="kodEditorAc('Pipeline (JSON)', item['_pipelineStr'] || '', 'json', 'itemPipeline', item)">
                            <pre>{{ (item['_pipelineStr'] || '') | slice:0:80 }}{{ (item['_pipelineStr']?.length || 0) > 80 ? '...' : '' }}</pre>
                            <button nz-button nzSize="small" nzType="link"><span nz-icon nzType="edit"></span></button>
                          </div>
                        </div>
                      }
                      @if (item.veriKaynagi?.mod === 'hesaplama') {
                        <div class="alan-mini">
                          <label>Hesaplama (JS)</label>
                          <div class="kod-onizleme" (click)="kodEditorAc('Hesaplama (JS)', item.veriKaynagi?.piFunc || '', 'javascript', 'itemHesaplama', item)">
                            <pre>{{ (item.veriKaynagi?.piFunc || '') | slice:0:80 }}{{ (item.veriKaynagi?.piFunc?.length || 0) > 80 ? '...' : '' }}</pre>
                            <button nz-button nzSize="small" nzType="link"><span nz-icon nzType="edit"></span></button>
                          </div>
                        </div>
                      }
                      @if (item.veriKaynagi?.mod === 'soket') {
                        <div class="alan-mini">
                          <label>Event Adı</label>
                          <input nz-input nzSize="small" [ngModel]="item.veriKaynagi?.soketOlay || ''" (ngModelChange)="itemVeriAlanDegistir(item, 'soketOlay', $event)" />
                        </div>
                      }
                    </div>

                    <div class="item-bolum">
                      <div class="item-bolum-baslik">Olaylar</div>
                      @for (olay of olayTurleri; track olay) {
                        <div class="alan-mini">
                          <label>{{ olayEtiketi(olay) }}</label>
                          @if (olaySablonlar[olay]?.length) {
                            <div class="olay-sablonlar">
                              @for (s of olaySablonlar[olay]; track s.baslik) {
                                <button class="sablon-chip" (click)="itemOlaySablonuUygula(item, olay, s.sablon)">{{ s.baslik }}</button>
                              }
                            </div>
                          }
                          <div class="kod-onizleme" (click)="kodEditorAc(olayEtiketi(olay), item.olaylar?.[olay] || '', 'javascript', 'itemOlay', item, olay)">
                            <pre>{{ (item.olaylar?.[olay] || '') | slice:0:80 }}{{ (item.olaylar?.[olay]?.length || 0) > 80 ? '...' : '' }}</pre>
                            <button nz-button nzSize="small" nzType="link"><span nz-icon nzType="edit"></span></button>
                          </div>
                        </div>
                      }
                    </div>
                  </div>
                }
              </div>
            }
          </div>
        } @else {
          <div class="bos-durum">
            <span>🗂️</span>
            <span>Henüz item eklenmedi</span>
            <span class="bos-ipucu">Kütüphaneden seçin veya boş item ekleyin</span>
          </div>
        }
      </div>

    </div>

    <nz-modal [nzVisible]="kodModalAcik()" [nzTitle]="kodModalBaslik()" nzWidth="80vw"
      (nzOnOk)="kodModalKaydet()" (nzOnCancel)="kodModalKapat()" nzOkText="Kaydet" nzCancelText="İptal"
      (nzAfterOpen)="kodModalAcildi()"
      [nzBodyStyle]="{ height: '70vh', padding: '0', overflow: 'hidden' }">
      <ng-container *nzModalContent>
        @if (kodModalHazir()) {
          <div style="height: 70vh; width: 100%;">
            <nz-code-editor style="display:block; height:100%; width:100%;"
              [ngModel]="kodModalGosterimDeger()"
              (ngModelChange)="kodModalGosterimDeger.set($event); kodModalDeger.set($event)"
              [nzEditorOption]="kodModalEditorOptions">
            </nz-code-editor>
          </div>
        }
      </ng-container>
    </nz-modal>
  `,
  styles: `
    :host { display: block; overflow-y: auto; }

    .veri-paneli {
      display: flex;
      flex-direction: column;
      gap: 16px;
      padding: 12px;
    }

    .bolum {
      display: flex;
      flex-direction: column;
      gap: 10px;
    }

    .bolum-baslik {
      font-size: 12px;
      font-weight: 700;
      text-transform: uppercase;
      letter-spacing: 0.8px;
      color: var(--te-text-secondary, rgba(0, 0, 0, 0.5));
      padding-bottom: 4px;
      border-bottom: 1px solid var(--te-border, rgba(0, 0, 0, 0.08));
    }

    .mod-kartlar {
      display: grid;
      grid-template-columns: repeat(2, 1fr);
      gap: 6px;
    }

    .mod-kart {
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 4px;
      padding: 10px 6px;
      border-radius: 8px;
      border: 2px solid var(--te-border, rgba(0, 0, 0, 0.1));
      background: var(--te-btn-bg, rgba(0, 0, 0, 0.03));
      cursor: pointer;
      transition: all 0.2s;
    }

    .mod-kart:hover { border-color: rgba(24, 144, 255, 0.4); }

    .mod-kart.secili {
      border-color: #1890ff;
      background: rgba(24, 144, 255, 0.1);
      box-shadow: 0 0 0 2px rgba(24, 144, 255, 0.15);
    }

    .mod-simge { font-size: 20px; }

    .mod-ad {
      font-size: 10px;
      font-weight: 600;
      color: var(--py-metin, rgba(0, 0, 0, 0.85));
    }

    .mod-aciklama {
      font-size: 11px;
      color: var(--te-text-muted, rgba(0, 0, 0, 0.35));
      font-style: italic;
    }

    .alan-grup {
      display: flex;
      flex-direction: column;
      gap: 4px;
    }

    .alan-etiket {
      font-size: 11px;
      font-weight: 600;
      color: var(--te-text-secondary, rgba(0, 0, 0, 0.55));
      letter-spacing: 0.2px;
    }

    .kod-editor {
      border-radius: 6px;
      overflow: hidden;
      border: 1px solid var(--te-border, rgba(0, 0, 0, 0.1));
    }

    .kod-editor.mini { border-radius: 4px; }

    .kod-onizleme {
      border: 1px solid var(--te-border, rgba(0,0,0,0.1));
      border-radius: 6px;
      padding: 8px;
      cursor: pointer;
      transition: border-color 0.15s;
      position: relative;
    }
    .kod-onizleme:hover { border-color: #1890ff; }
    .kod-onizleme pre {
      margin: 0;
      font-size: 11px;
      font-family: 'Fira Code', monospace;
      color: var(--te-text-secondary, rgba(0,0,0,0.55));
      white-space: pre-wrap;
      word-break: break-all;
      max-height: 60px;
      overflow: hidden;
    }
    .kod-onizleme button { position: absolute; top: 4px; right: 4px; }

    .kod-ipucu {
      font-size: 10px;
      color: var(--te-text-muted, rgba(0, 0, 0, 0.35));
    }

    .kod-ipucu code {
      background: var(--te-code-bg, rgba(255, 255, 255, 0.08));
      padding: 1px 4px;
      border-radius: 3px;
      font-size: 10px;
      font-family: monospace;
      color: var(--te-code-color, #7ee787);
    }

    .test-btn { border-style: dashed !important; }

    .kutuphane-grid {
      display: flex;
      flex-direction: column;
      gap: 8px;
    }

    .kutuphane-kat-baslik {
      font-size: 10px;
      font-weight: 700;
      text-transform: uppercase;
      letter-spacing: 0.8px;
      color: var(--te-text-muted, rgba(255, 255, 255, 0.3));
      margin-bottom: 4px;
      display: block;
    }

    .kutuphane-bilesenler {
      display: flex;
      flex-wrap: wrap;
      gap: 4px;
    }

    .kutuphane-btn {
      display: flex;
      align-items: center;
      gap: 4px;
      padding: 4px 8px;
      border-radius: 6px;
      border: 1px solid var(--te-border, rgba(0, 0, 0, 0.1));
      background: var(--te-btn-bg, rgba(0, 0, 0, 0.03));
      cursor: pointer;
      transition: all 0.2s;
    }

    .kutuphane-btn:hover {
      border-color: #1890ff;
      background: rgba(24, 144, 255, 0.08);
      transform: translateY(-1px);
    }

    .kutuphane-btn:active { transform: scale(0.97); }

    .kb-simge { font-size: 14px; }

    .kb-ad {
      font-size: 10px;
      font-weight: 500;
      color: var(--te-text-secondary, rgba(0, 0, 0, 0.55));
      white-space: nowrap;
    }

    .item-ust-bar {
      display: flex;
      align-items: center;
      justify-content: space-between;
      margin-top: 4px;
    }

    .item-sayi {
      font-size: 11px;
      color: var(--te-text-muted, rgba(0, 0, 0, 0.4));
    }

    .item-liste {
      display: flex;
      flex-direction: column;
      gap: 4px;
    }

    .item-kart {
      border: 1px solid var(--te-border, rgba(0, 0, 0, 0.1));
      border-radius: 8px;
      overflow: hidden;
      transition: border-color 0.2s, box-shadow 0.2s;
    }

    .item-kart:hover { border-color: rgba(24, 144, 255, 0.3); }

    .item-kart.acik {
      border-color: #1890ff;
      box-shadow: 0 2px 10px rgba(24, 144, 255, 0.1);
    }

    .item-kart-baslik {
      display: flex;
      align-items: center;
      gap: 6px;
      padding: 6px 8px;
      background: var(--te-btn-bg, rgba(0, 0, 0, 0.03));
      border-bottom: 1px solid var(--te-border, rgba(0, 0, 0, 0.06));
      cursor: grab;
    }

    .item-drag-handle {
      cursor: grab;
      font-size: 12px;
      opacity: 0.3;
      user-select: none;
      transition: opacity 0.2s;
    }

    .item-drag-handle:hover { opacity: 0.7; }

    .item-simge-onizleme { font-size: 14px; }

    .item-baslik-metin {
      font-size: 12px;
      font-weight: 500;
      color: var(--py-metin, rgba(0, 0, 0, 0.85));
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    .item-bosluk { flex: 1; }

    .item-btn {
      width: 22px;
      height: 22px;
      border: none;
      background: none;
      cursor: pointer;
      font-size: 11px;
      border-radius: 4px;
      color: var(--te-text-muted, rgba(0, 0, 0, 0.35));
      display: flex;
      align-items: center;
      justify-content: center;
      transition: all 0.15s;
    }

    .item-btn:hover {
      background: var(--te-hover, rgba(0, 0, 0, 0.04));
      color: #1890ff;
    }

    .item-btn.sil:hover { color: #f5222d; }
    .item-btn.ac-kapat { font-size: 9px; opacity: 0.5; }

    .item-detay {
      padding: 8px 10px 12px;
      display: flex;
      flex-direction: column;
      gap: 10px;
      background: var(--te-item-detay-bg, rgba(0, 0, 0, 0.02));
    }

    .item-bolum {
      display: flex;
      flex-direction: column;
      gap: 6px;
    }

    .item-bolum-baslik {
      font-size: 10px;
      font-weight: 700;
      text-transform: uppercase;
      letter-spacing: 0.6px;
      color: var(--te-text-muted, rgba(0, 0, 0, 0.35));
      padding-bottom: 2px;
      border-bottom: 1px solid var(--te-border, rgba(0, 0, 0, 0.06));
    }

    .item-alan-grid {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 6px;
    }

    .alan-mini {
      display: flex;
      flex-direction: column;
      gap: 2px;
    }

    .alan-mini label {
      font-size: 10px;
      color: var(--te-text-muted, rgba(0, 0, 0, 0.4));
      font-weight: 500;
    }

    .renk-satir {
      display: flex;
      align-items: center;
      gap: 4px;
    }

    .renk-secici {
      width: 28px;
      height: 24px;
      padding: 1px;
      border: 1px solid var(--te-border, rgba(0, 0, 0, 0.12));
      border-radius: 4px;
      cursor: pointer;
      flex-shrink: 0;
      background: transparent;
    }

    .renk-secici::-webkit-color-swatch-wrapper { padding: 0; }
    .renk-secici::-webkit-color-swatch { border: none; border-radius: 3px; }
    .renk-satir input[nz-input] { flex: 1; }

    .olay-sablonlar {
      display: flex;
      flex-wrap: wrap;
      gap: 3px;
      margin-bottom: 2px;
    }

    .sablon-chip {
      padding: 1px 6px;
      border-radius: 8px;
      border: 1px solid var(--te-border, rgba(255, 255, 255, 0.1));
      background: var(--te-btn-bg, rgba(0, 0, 0, 0.03));
      cursor: pointer;
      font-size: 9px;
      color: var(--te-text-secondary, rgba(0, 0, 0, 0.45));
      transition: all 0.15s;
    }

    .sablon-chip:hover {
      border-color: #1890ff;
      color: #1890ff;
      background: rgba(24, 144, 255, 0.08);
    }

    .bos-durum {
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 6px;
      padding: 24px 12px;
      color: var(--te-text-muted, rgba(0, 0, 0, 0.35));
      font-size: 12px;
    }

    .bos-durum span:first-child { font-size: 24px; opacity: 0.4; }

    .bos-ipucu {
      font-size: 10px;
      opacity: 0.5;
    }

    .item-liste .cdk-drag-preview {
      border-radius: 8px;
      box-shadow: 0 8px 24px rgba(0, 0, 0, 0.4);
      opacity: 0.9;
    }

    .item-liste .cdk-drag-placeholder {
      background: rgba(24, 144, 255, 0.06);
      border: 2px dashed rgba(24, 144, 255, 0.3);
      border-radius: 8px;
      min-height: 36px;
    }

    .item-liste .cdk-drag-animating {
      transition: transform 200ms ease;
    }

    :host-context(body.tema-karanlik) {
      --te-border: rgba(255,255,255,0.08);
      --te-btn-bg: rgba(255,255,255,0.03);
      --te-hover: rgba(255,255,255,0.06);
      --te-text-secondary: rgba(255,255,255,0.6);
      --te-text-muted: rgba(255,255,255,0.4);
      --te-code-bg: rgba(255,255,255,0.08);
      --te-code-color: #7ee787;
      --te-item-detay-bg: rgba(0,0,0,0.15);
    }
    :host-context(body.tema-karanlik) .kod-onizleme pre { color: rgba(255,255,255,0.6); }
  `,
})
export class TeVeriPaneliComponent {
  private CD = inject(ChangeDetectorRef);
  sablon = input<ISablon>();

  veriKaynagiDegisti = output<ISablonVeriKaynagi>();
  itemlerDegisti = output<ISablonItem[]>();
  veriTestTiklandi = output<void>();

  seciliItemIdx = signal<number>(-1);

  readonly modListesi = ['aggregate', 'sabit', 'soket', 'hesaplama'] as const;
  readonly veriModBilgi = VERI_MOD_BILGI;
  readonly bilesenKutuphanesi = BILESEN_KUTUPHANESI;
  readonly olaySablonlar = OLAY_SABLONLAR;
  readonly olayTurleri = ['tiklanma', 'yenilendi', 'esikAsimi', 'ciftTiklama'];

  kodModalAcik = signal(false);
  kodModalBaslik = signal('');
  kodModalDeger = signal('');
  kodModalDil = signal('json');
  kodModalHedef = signal<{ tip: string; item?: ISablonItem; alan?: string } | null>(null);

  kodModalGosterimDeger = signal('');
  kodModalHazir = signal(false);

  kodModalEditorOptions = {
    minimap: { enabled: true },
    fontSize: 14,
    lineNumbers: 'on' as const,
    automaticLayout: true,
    wordWrap: 'on' as const,
    scrollBeyondLastLine: false,
    tabSize: 2,
  };

  kodModalAcildi() {
    this.kodModalHazir.set(false);
    setTimeout(() => {
      this.kodModalGosterimDeger.set(this.kodModalDeger());
      this.kodModalHazir.set(true);
      this.CD.markForCheck();
      setTimeout(() => {
        const eds = (window as any).monaco?.editor?.getEditors?.() || [];
        eds[eds.length - 1]?.layout();
        eds[eds.length - 1]?.focus();
      }, 100);
    }, 200);
  }

  kodEditorAc(baslik: string, deger: string, dil: string, tip: string, item?: ISablonItem, alan?: string) {
    this.kodModalBaslik.set(baslik);
    this.kodModalDeger.set(deger || '');
    this.kodModalDil.set(dil);
    this.kodModalHedef.set({ tip, item, alan });
    this.kodModalAcik.set(true);
  }

  kodModalKapat() {
    this.kodModalAcik.set(false);
    this.kodModalHedef.set(null);
  }

  kodModalKaydet() {
    const deger = this.kodModalDeger();
    const hedef = this.kodModalHedef();
    if (!hedef) return;

    switch (hedef.tip) {
      case 'pipeline': this.pipelineStrDegisti(deger); break;
      case 'donusum': this.vkAlanDegistir('donusumFunc', deger); break;
      case 'sabit': this.sabitVeriStrDegisti(deger); break;
      case 'soketFiltre': this.soketFiltreStrDegisti(deger); break;
      case 'hesaplama': this.vkAlanDegistir('piFunc', deger); break;
      case 'itemSabit':
        if (hedef.item) { hedef.item['_sabitVeriStr'] = deger; this.itemlerYayinla(); }
        break;
      case 'itemPipeline':
        if (hedef.item) { hedef.item['_pipelineStr'] = deger; this.itemlerYayinla(); }
        break;
      case 'itemHesaplama':
        if (hedef.item) this.itemVeriAlanDegistir(hedef.item, 'piFunc', deger);
        break;
      case 'itemOlay':
        if (hedef.item && hedef.alan) this.itemOlaySablonuUygula(hedef.item, hedef.alan, deger);
        break;
    }
    this.kodModalKapat();
  }

  get editorTema(): string {
    return document.body.classList.contains('tema-karanlik') ? 'vs-dark' : 'vs';
  }

  private get editorTemel() {
    return {
      theme: this.editorTema,
      minimap: { enabled: false },
      lineNumbers: 'on' as const,
      scrollBeyondLastLine: false,
      automaticLayout: true,
      fontSize: 12,
      tabSize: 2,
      wordWrap: 'on' as const,
    };
  }

  get jsEditorOptions() { return { ...this.editorTemel, language: 'javascript' }; }
  get jsonEditorOptions() { return { ...this.editorTemel, language: 'json' }; }

  private pipelineStrSinyali = signal('');
  private sabitVeriStrSinyali = signal('');
  private soketFiltreStrSinyali = signal('');

  seciliMod(): ISablonVeriKaynagi['mod'] {
    return this.sablon()?.veriKaynagi?.mod || 'sabit';
  }

  vkVt() { return this.sablon()?.veriKaynagi?.vt || ''; }
  vkDonusumFunc() { return this.sablon()?.veriKaynagi?.donusumFunc || ''; }
  vkSoketOlay() { return this.sablon()?.veriKaynagi?.soketOlay || ''; }
  vkSoketTetik() { return this.sablon()?.veriKaynagi?.soketTetik || ''; }
  vkPiFunc() { return this.sablon()?.veriKaynagi?.piFunc || ''; }
  vkYenilemeAraligi() { return this.sablon()?.veriKaynagi?.yenilemeAraligi || 0; }

  pipelineStr() {
    const v = this.pipelineStrSinyali();
    if (v) return v;
    const p = this.sablon()?.veriKaynagi?.pipeline;
    return p ? JSON.stringify(p, null, 2) : '';
  }

  sabitVeriStr() {
    const v = this.sabitVeriStrSinyali();
    if (v) return v;
    const s = this.sablon()?.veriKaynagi?.sabitVeri;
    return s ? JSON.stringify(s, null, 2) : '';
  }

  soketFiltreStr() {
    const v = this.soketFiltreStrSinyali();
    if (v) return v;
    const f = this.sablon()?.veriKaynagi?.soketFiltre;
    return f ? JSON.stringify(f, null, 2) : '';
  }

  mevcutItemler(): ISablonItem[] {
    return this.sablon()?.itemler || [];
  }

  modDegistir(mod: string) {
    const mevcut = this.sablon()?.veriKaynagi || { mod: 'sabit' as const };
    this.veriKaynagiDegisti.emit({ ...mevcut, mod: mod as ISablonVeriKaynagi['mod'] });
  }

  vkAlanDegistir(alan: string, deger: any) {
    const mevcut = this.sablon()?.veriKaynagi || { mod: 'sabit' as const };
    this.veriKaynagiDegisti.emit({ ...mevcut, [alan]: deger });
  }

  pipelineStrDegisti(str: string) {
    this.pipelineStrSinyali.set(str);
    const mevcut = this.sablon()?.veriKaynagi || { mod: 'aggregate' as const };
    let pipeline: any[] | undefined;
    try { pipeline = JSON.parse(str); } catch {}
    this.veriKaynagiDegisti.emit({ ...mevcut, pipeline });
  }

  sabitVeriStrDegisti(str: string) {
    this.sabitVeriStrSinyali.set(str);
    const mevcut = this.sablon()?.veriKaynagi || { mod: 'sabit' as const };
    let sabitVeri: any;
    try { sabitVeri = JSON.parse(str); } catch { sabitVeri = str; }
    this.veriKaynagiDegisti.emit({ ...mevcut, sabitVeri });
  }

  soketFiltreStrDegisti(str: string) {
    this.soketFiltreStrSinyali.set(str);
    const mevcut = this.sablon()?.veriKaynagi || { mod: 'soket' as const };
    let soketFiltre: Record<string, any> | undefined;
    try { soketFiltre = JSON.parse(str); } catch {}
    this.veriKaynagiDegisti.emit({ ...mevcut, soketFiltre });
  }

  olayEtiketi(olay: string): string {
    const map: Record<string, string> = {
      tiklanma: '👆 Tıklanma',
      yenilendi: '🔄 Yenilendi',
      esikAsimi: '⚠️ Eşik Aşımı',
      ciftTiklama: '👆👆 Çift Tık',
    };
    return map[olay] || olay;
  }

  bosItemEkle() {
    const itemler = [...this.mevcutItemler()];
    itemler.push({
      id: new FikoYTId().toString(),
      simge: '📦',
      baslik: 'Yeni Item',
      olaylar: {},
      veriKaynagi: { mod: 'sabit' },
    } as ISablonItem);
    this.seciliItemIdx.set(itemler.length - 1);
    this.itemlerDegisti.emit(itemler);
  }

  bilesendenEkle(preset: IBilesenPreset) {
    const itemler = [...this.mevcutItemler()];
    const varsayilan = JSON.parse(JSON.stringify(preset.varsayilan));
    const yeni: ISablonItem = {
      id: new FikoYTId().toString(),
      simge: preset.simge,
      baslik: preset.baslik,
      olaylar: {},
      veriKaynagi: { mod: 'sabit' },
      ...varsayilan,
    };
    if (!yeni.olaylar) yeni.olaylar = {};
    if (!yeni.veriKaynagi) yeni.veriKaynagi = { mod: 'sabit' };
    itemler.push(yeni);
    this.seciliItemIdx.set(itemler.length - 1);
    this.itemlerDegisti.emit(itemler);
  }

  itemSil(idx: number) {
    const itemler = [...this.mevcutItemler()];
    itemler.splice(idx, 1);
    if (this.seciliItemIdx() >= itemler.length) this.seciliItemIdx.set(-1);
    this.itemlerDegisti.emit(itemler);
  }

  onItemDrop(event: CdkDragDrop<ISablonItem[]>) {
    const itemler = [...this.mevcutItemler()];
    moveItemInArray(itemler, event.previousIndex, event.currentIndex);
    this.itemlerDegisti.emit(itemler);
  }

  itemVeriModDegistir(item: ISablonItem, mod: string) {
    if (!item.veriKaynagi) item.veriKaynagi = { mod: 'sabit' };
    item.veriKaynagi.mod = mod as ISablonVeriKaynagi['mod'];
    this.itemlerYayinla();
  }

  itemVeriAlanDegistir(item: ISablonItem, alan: string, deger: any) {
    if (!item.veriKaynagi) item.veriKaynagi = { mod: 'sabit' };
    (item.veriKaynagi as any)[alan] = deger;
    this.itemlerYayinla();
  }

  itemOlaySablonuUygula(item: ISablonItem, olayAdi: string, sablon: string) {
    if (!item.olaylar) item.olaylar = {};
    item.olaylar[olayAdi] = sablon;
    this.itemlerYayinla();
  }

  itemlerYayinla() {
    this.itemlerDegisti.emit([...this.mevcutItemler()]);
  }
}
